import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import PurchasesManage, { type PurchasesManageSaveReason } from '../PurchasesManage';
import type { Bill } from '../../types/bill';
import { usePurchaseTransactionsHub } from './PurchaseTransactionsHubContext';
import {
  parsePurchaseHubLocationState,
  resolveBillOverlayCloseTarget,
} from './purchaseHubNavigation';

const PurchaseBillOverlay = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = usePurchaseTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const navState = useMemo(
    () => parsePurchaseHubLocationState(location.state),
    [location.state]
  );

  const isNew = id === 'new';
  const parsedId = id != null ? Number(id) : Number.NaN;
  const billId = isNew || Number.isNaN(parsedId) || parsedId <= 0 ? undefined : parsedId;

  const close = useCallback(() => {
    const target = resolveBillOverlayCloseTarget(billId, listSearch, navState);
    navigate(target, { preventScrollReset: true });
  }, [billId, listSearch, navState, navigate]);

  const handleBillSaved = useCallback(
    (bill: Bill, reason: PurchasesManageSaveReason) => {
      refetchList();

      if (reason === 'draft_created') {
        navigate(
          {
            pathname: `/purchase-transactions/bill/${bill.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true, state: location.state }
        );
        return;
      }

      if (
        reason === 'received' ||
        reason === 'amended' ||
        reason === 'cost_corrected' ||
        reason === 'lines_appended'
      ) {
        navigate(
          {
            pathname: `/purchase-transactions/view/bill/${bill.id}`,
            search: listSearch,
          },
          { replace: true, preventScrollReset: true, state: location.state }
        );
      }
    },
    [close, listSearch, location.state, navigate, refetchList]
  );

  return (
    <TransactionOverlay headerless variant="wide" onClose={close}>
      <PurchasesManage
        key={billId ?? 'new'}
        embedded
        billId={billId}
        onClose={close}
        onBillSaved={handleBillSaved}
      />
    </TransactionOverlay>
  );
};

export default PurchaseBillOverlay;
